import "@/App.css";
import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Routes, Route, useLocation } from "react-router-dom";
import { Layout } from "./components/layout/Layout";
import { Home } from "./pages/Home";
import { DataProvider } from "./context/DataContext";
import { ScrollToTop } from "./components/ScrollToTop";
import { RouteSeo } from "./components/RouteSeo";

const lazyNamed = (factory, name) =>
  lazy(() => factory().then((module) => ({ default: module[name] })));

const Services = lazyNamed(() => import("./pages/Services"), "Services");
const Products = lazyNamed(() => import("./pages/Products"), "Products");
const Projects = lazyNamed(() => import("./pages/Projects"), "Projects");
const Blog = lazyNamed(() => import("./pages/Blog"), "Blog");
const BlogPost = lazyNamed(() => import("./pages/BlogPost"), "BlogPost");
const HowManySolarBatteries = lazyNamed(() => import("./pages/HowManySolarBatteries"), "HowManySolarBatteries");
const WesternCapeLithiumBatteries2026 = lazyNamed(() => import("./pages/WesternCapeLithiumBatteries2026"), "WesternCapeLithiumBatteries2026");
const About = lazyNamed(() => import("./pages/About"), "About");
const FAQ = lazyNamed(() => import("./pages/FAQ"), "FAQ");
const Contact = lazyNamed(() => import("./pages/Contact"), "Contact");
const ThankYou = lazyNamed(() => import("./pages/ThankYou"), "ThankYou");
const Admin = lazyNamed(() => import("./pages/Admin"), "Admin");
const ProductDetail = lazyNamed(() => import("./pages/ProductDetail"), "ProductDetail");
const InverterGame = lazyNamed(() => import("./pages/InverterGame"), "InverterGame");
const HomeBatteryBackupLanding = lazyNamed(() => import("./pages/HomeBatteryBackupLanding"), "HomeBatteryBackupLanding");
const YearOfTheBeastLanding = lazyNamed(() => import("./pages/YearOfTheBeastLanding"), "YearOfTheBeastLanding");
const SolarRoiCalculator = lazyNamed(() => import("./pages/SolarRoiCalculator"), "SolarRoiCalculator");
const SolarBatteryGuide = lazyNamed(() => import("./pages/SolarBatteryGuide"), "SolarBatteryGuide");
const SolarInstallersWesternCape = lazyNamed(() => import("./pages/SolarInstallersWesternCape"), "SolarInstallersWesternCape");
const SolarInstallersCapeTown = lazyNamed(() => import("./pages/SolarInstallersCapeTown"), "SolarInstallersCapeTown");
const SolarInstallersPaarl = lazyNamed(() => import("./pages/SolarInstallersPaarl"), "SolarInstallersPaarl");
const SolarInstallersHermanus = lazyNamed(() => import("./pages/SolarInstallersHermanus"), "SolarInstallersHermanus");
const Agrifox = lazyNamed(() => import("./pages/Agrifox"), "Agrifox");
const ChatBot = lazyNamed(() => import("./components/chat/ChatBot"), "ChatBot");

const PageFallback = () => <div className="min-h-[40vh] bg-slate-950" aria-hidden="true" />;
const LegacyCampaignRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/agrifox${location.search}${location.hash}`} replace />;
};

const DeferredChatBot = ({ autoOpen }) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let idleId;
    let timeoutId;
    const show = () => setReady(true);
    const schedule = () => {
      if ('requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(show, { timeout: 3000 });
      } else {
        show();
      }
    };

    timeoutId = window.setTimeout(schedule, 7000);

    return () => {
      if (idleId) window.cancelIdleCallback(idleId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  if (!ready) return null;
  return <ChatBot autoOpen={autoOpen} />;
};

function AppContent() {
  const location = useLocation();
  const isAdminRoute = location.pathname === '/admin';
  const isHomeRoute = location.pathname === '/';
  const isLandingRoute = location.pathname.startsWith('/lp/');

  return (
    <Layout showChrome={!isLandingRoute}>
      <RouteSeo />
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/lp/home-battery-backup-western-cape" element={<HomeBatteryBackupLanding />} />
          <Route path="/lp/year-of-the-beast" element={<YearOfTheBeastLanding />} />
          <Route path="/agrifox" element={<Agrifox />} />
          <Route path="/commercial-solar-september" element={<LegacyCampaignRedirect />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/how-many-solar-batteries-do-i-need-south-africa" element={<HowManySolarBatteries />} />
          <Route path="/blog/western-cape-top-lithium-battery-backup-solutions-2026" element={<WesternCapeLithiumBatteries2026 />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/inverter-game" element={<InverterGame />} />
          <Route path="/solar-roi-calculator" element={<SolarRoiCalculator />} />
          <Route path="/how-solar-batteries-work" element={<SolarBatteryGuide />} />
          <Route path="/solar-installers-western-cape" element={<SolarInstallersWesternCape />} />
          <Route path="/solar-installers-cape-town" element={<SolarInstallersCapeTown />} />
          <Route path="/solar-installers-paarl" element={<SolarInstallersPaarl />} />
          <Route path="/solar-installers-hermanus" element={<SolarInstallersHermanus />} />
        </Routes>
      </Suspense>
      {!isAdminRoute && !isLandingRoute && (
        <Suspense fallback={null}>
          <DeferredChatBot autoOpen={isHomeRoute} />
        </Suspense>
      )}
    </Layout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <DataProvider>
        <ScrollToTop />
        <AppContent />
      </DataProvider>
    </BrowserRouter>
  );
}

export default App;
